import IStorage from './IStorage';

/**
 * LocalStorage Adapter
 * Wraps localStorage API to match IStorage interface with Promise-based methods
 */
class LocalStorageAdapter extends IStorage {
    constructor() {
        super();
        this.storage = window.localStorage;
    }

    /**
     * Get a value from localStorage
     * @param {string} key
     * @returns {Promise<any>}
     */
    async get(key) {
        try {
            const item = this.storage.getItem(key);
            if (item === null) {
                return null;
            }

            // Try to parse as JSON, fallback to raw string
            try {
                return JSON.parse(item);
            } catch (parseError) {
                // If it's not valid JSON, return the raw string
                console.warn(`Failed to parse JSON for key "${key}":`, parseError.message);
                return item;
            }
        } catch (error) {
            console.error('localStorage get error:', error);
            return null;
        }
    }

    /**
     * Set a value in localStorage
     * @param {string} key
     * @param {any} value
     * @returns {Promise<void>}
     */
    async set(key, value) {
        try {
            const serialized = typeof value === 'string' ? value : JSON.stringify(value);
            this.storage.setItem(key, serialized);
        } catch (error) {
            if (error.name === 'QuotaExceededError') {
                console.error('localStorage quota exceeded');
                throw new Error('Storage quota exceeded. Please clear some data.');
            }
            console.error('localStorage set error:', error);
            throw error;
        }
    }

    /**
     * Remove a value from localStorage
     * @param {string} key
     * @returns {Promise<void>}
     */
    async remove(key) {
        try {
            this.storage.removeItem(key);
        } catch (error) {
            console.error('localStorage remove error:', error);
            throw error;
        }
    }

    /**
     * Clear all values from localStorage
     * @returns {Promise<void>}
     */
    async clear() {
        try {
            this.storage.clear();
        } catch (error) {
            console.error('localStorage clear error:', error);
            throw error;
        }
    }

    /**
     * Get all keys from localStorage
     * @returns {Promise<string[]>}
     */
    async keys() {
        try {
            const keys = [];
            for (let i = 0; i < this.storage.length; i++) {
                const key = this.storage.key(i);
                if (key) {
                    keys.push(key);
                }
            }
            return keys;
        } catch (error) {
            console.error('localStorage keys error:', error);
            return [];
        }
    }

    /**
     * Check if a key exists in localStorage
     * @param {string} key
     * @returns {Promise<boolean>}
     */
    async has(key) {
        try {
            return this.storage.getItem(key) !== null;
        } catch (error) {
            console.error('localStorage has error:', error);
            return false;
        }
    }

    /**
     * Get the number of items in localStorage
     * @returns {Promise<number>}
     */
    async length() {
        try {
            return this.storage.length;
        } catch (error) {
            console.error('localStorage length error:', error);
            return 0;
        }
    }
}

export default LocalStorageAdapter;
